import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './PrivacyPolicy.module.css';

const PrivacyPolicy = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  // prettier-ignore
  return (
    <div className={classes}>

      <b className={css.lastUpdated}>Last updated: 3 December, 2025</b>
      <p><strong>Bendelta Pty Ltd</strong> (ABN 58 105 151 326) trading as <b>BOLDLY (“we”, “us”, “our”)</b> is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, store and protect personal information when you access or use our websites, mobile applications, technology platforms and related services (collectively, the <b>“Sites”</b> or <b>“Services”</b>).</p>
      <p>This Privacy Policy also applies when you interact with us, apply to work with us, or otherwise provide personal information to us.<br/></p>
      <p>By using the Sites or Services, you consent to the practices described in this Privacy Policy.<br/></p>
      <p>If you are under <b>18 years of age</b>, please do not send any personal information to us.<br/></p>

      <h2>1. Information We Collect</h2>
      <p>
        We collect personal information to provide our Services, operate our coaching marketplace, support coaches and clients, meet legal obligations and improve user experience.
      </p>
      <p>
        We collect information in the following categories:
      </p>
      <h3>1.1 Information You Provide Directly</h3>
      <p>This includes information you provide when you:</p>
      <ul>
        <li>Register for or use our Sites or Services</li>
        <li>Subscribe to alerts, newsletters or events</li>
        <li>Correspond with us or submit a support request</li>
        <li>Participate in coaching sessions, assessments, questionnaires or program activities</li>
        <li>Provide feedback, reviews or uploads as part of your coaching engagement</li>
        <li>Apply for employment, coaching opportunities or partnership roles.</li>
      </ul>
      <p>This may include:</p>
      <ul>
        <li>Your name, contact details (address, email, phone number)</li>
        <li>Professional information (role, employer, LinkedIn profile, biography, qualifications)</li>
        <li>Profile photos, video submissions or other uploaded materials</li>
        <li>Login credentials</li>
        <li>Responses to surveys, assessments, program tasks or coaching assignments</li>
        <li>Information shared verbally or in writing during coaching or platform interactions</li>
        <li>Recruitment information including CVs, interview notes and references</li>
      </ul>
      <h3>1.2 User-Generated Content</h3>
        <p>When you use our marketplace, you may generate or submit <b>“User Content,”</b> such as:</p>
        <ul>
          <li>coach bios</li>
          <li>client reviews or feedback</li>
          <li>messages or comments</li>
          <li>uploaded documents, worksheets or files</li>
          <li>audio, video or chat content generated through the Sites.</li>
        </ul>
        <p>User Content may be visible to other users depending on your settings and Customer configurations.</p>
      <h3>1.3 Information We Collect Automatically</h3>
        <p>When you use the Sites or Services, we automatically collect data such as:</p>
        <ul>
          <li>Device type, operating system, browser type and version</li>
          <li>IP address and approximate geolocation</li>
          <li>Pages viewed, search queries, clickstream data and browsing behaviour</li>
          <li>Session length and login times</li>
          <li>Cookies and similar tracking technologies (see Section 6)</li>
          <li>Crash logs, diagnostics and performance analytics.</li>
        </ul>
        <p>
        This information is often aggregated and not used to personally identify you unless combined with other personal information.
        </p>
      <h3>1.4 Information Received from Third Parties</h3>
        <p>We may receive information from:</p>
        <ul>
          <li>Customers (e.g., your employer) who provide access to the Services</li>
          <li>Coaches or clients who interact with you</li>
          <li>Third-party authentication providers (e.g., LinkedIn, Google login)</li>
          <li>Recruitment agencies</li>
          <li>Public sources (such as professional profiles you make publicly available).</li>
        </ul>
      <h2>2. Data Retention</h2>
      <p>We retain personal information only for as long as reasonably necessary to:</p>
      <ul>
        <li>provide our Services</li>
        <li>comply with legal, regulatory or reporting obligations</li>
        <li>resolve disputes</li>
        <li>enforce agreements</li>
        <li>support business operations (e.g., audit, prevention of misuse).</li>
      </ul>
      <p>Where information is no longer required, we take reasonable steps to destroy or de-identify it securely.</p>
      <h2>3. How We Use Personal Information</h2>
      <p>We use personal information for the following purposes:</p>
      <h3>3.1 Service Delivery</h3>
      <ul>
        <li>To operate, maintain and improve our Sites and Services</li>
        <li>To deliver coaching programs, assessments, workshops or digital learning</li>
        <li>To enable coach-client communication and scheduling</li>
        <li>To personalise content or features.</li>
      </ul>

      <h3>3.2 Platform Functionality and Performance</h3>
      <ul>
        <li>To fix errors, analyse usage, conduct diagnostics and improve performance</li>
        <li>To support security, fraud prevention and service integrity</li>
        <li>To analyse platform trends and user behaviour (in de-identified form where possible).</li>
      </ul>

      <h3>3.3 Communications</h3>
      <ul>
        <li>To send operational notifications, updates, alerts, newsletters or resources</li>
        <li>To respond to enquiries or provide customer support</li>
        <li>To send marketing communications (with consent where required).</li>
      </ul>

      <h3>3.4 Recruitment, Coaching Engagements and Vendor Management</h3>
      <ul>
        <li>To assess applications for employment, coaching roles or partnership opportunities</li>
        <li>To verify identity, qualifications and references</li>
        <li>To administer compensation or contractual arrangements.</li>
      </ul>

      <h3>3.5 Compliance and Legal</h3>
      <ul>
        <li>To comply with legal obligations</li>
        <li>To enforce our Terms of Service and Acceptable Use Policy</li>
        <li>To support investigations of suspected illegal activity or misuse.</li>
      </ul>

      <h3>3.6 With Your Consent</h3>
      <ul>
        <li>For any additional purpose disclosed to you at the time of collection.</li>
      </ul>

      <h3>Google Analytics</h3>
      <p>
        Our Sites use Google Analytics. You can learn more about Google’s data use at:{' '}
        <a href="https://policies.google.com/technologies/partner-sites" target="_blank">
          How Google uses information from sites or apps that use our services - Privacy & Terms - Google
        </a>
      </p>
      <p>
        You can opt out using Google’s tools:{' '}
        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank">
          Google Analytics Opt-out Browser Add-on Download Page
        </a>
      </p>

      <h2>4. Marketing Practices</h2>
      <p>We may send promotional or marketing communications where permitted by law. You may opt out at any time using the unsubscribe link or by contacting us.</p>
      <p>We will continue to send operational or account-related communications even if you opt out of marketing emails.</p>

      <h2>5. Disclosure of Personal Information & International Transfers</h2>
      <p>
        We may disclose personal information to the following categories of recipients, located in Australia or overseas:
      </p>
      <h3>5.1 Service Providers</h3>
      <p>
      Employees, contractors, coaches, consultants, IT providers, and professional advisers who support our operations.
      </p>

      <h3>5.2 Affiliates and Business Partners</h3>
      <p>
      Entities within the Bendelta Group or partners involved in delivering Services.
      </p>

      <h3>5.3 Employers / Sponsoring Organisations</h3>
      <p>
      Where your employer or sponsoring entity provides your access, we may share:
      </p>

      <ul>
        <li>participation status</li>
        <li>number of coaching sessions</li>
        <li>program activity categories</li>
      </ul>

      <p>
      We will not share coaching conversation content or session notes without your explicit consent unless required by law.
      </p>

      <h3>5.4 Business Transactions</h3>
      <p>
      If we undergo a merger, acquisition, restructuring or asset sale, your information may be transferred as part of the transaction.
      </p>

      <h3>5.5 Legal and Compliance</h3>
      <p>
      We may disclose information:
      </p>

      <ul>
        <li>to comply with laws, regulations or legal processes</li>
        <li>to protect rights, property or safety</li>
        <li>to detect, prevent or investigate misconduct</li>
        <li>to enforce our Terms of Service.</li>
      </ul>

      <h3>5.6 Overseas Transfers</h3>
      <p>
      Our platform and service providers may store or process data in multiple jurisdictions including Australia, Singapore, the EU, the UK, the US and others.
      </p>

      <p>
      By using the Sites or Services, you consent to the transfer of your information overseas. We take steps to ensure that such recipients protect personal information in a manner consistent with this Privacy Policy and applicable laws.
      </p>


      <h2>6. Cookies & Tracking Technologies</h2>
      <p>
      Our Sites use cookies and similar tools to:
      </p>

      <ul>
        <li>distinguish you from other users</li>
        <li>enable safe and efficient operation of the Services</li>
        <li>analyse traffic and performance</li>
        <li>enhance your experience.</li>
      </ul>

      <p>
      You can manage cookies through your browser settings. Disabling cookies may impact certain features.
      </p>

      <p>
      We may use:
      </p>

      <ul>
        <li>strictly necessary cookies</li>
        <li>analytical and performance cookies</li>
        <li>functional cookies.</li>
      </ul>

      <p>
      Third parties (such as advertising platforms or analytics providers) may also use cookies that we do not control.
      </p>

      <h2>7. Social Media and Third-Party Links</h2>
      <p>
      Our Sites may contain links to third-party websites or allow login via social media accounts (e.g., LinkedIn, Google).
      </p>

      <p>
      We are not responsible for the privacy practices of third-party sites or services. We recommend reviewing their privacy policies before interacting with them.
      </p>

      <p>
      If you connect a social media account to BOLDLY, you give us permission to access information in accordance with your social platform settings.
      </p>

      <p>
      You may disconnect social logins through your social media platform’s settings.
      </p>


      <h2>8. Children</h2>
      <p>
        The Sites and Services are not intended for individuals <b>under 18 years of age</b>, and we do not knowingly collect personal information from individuals under 18.
      </p>

      <h2>9. Consent & Changes to This Privacy Policy</h2>
      <p>
      By using our Services, you consent to our handling of personal information in accordance with this Privacy Policy.
      </p>

      <p>
      We may update this Privacy Policy from time to time. Changes will be posted on this page and the “Last updated” date will be amended. We may also provide additional notice by email or through the platform.
      </p>

      <p>
      Your continued use of the Services constitutes acceptance of the updated policy.
      </p>

      <h2>10. Your Rights</h2>
      <p>
      Depending on your jurisdiction, you may have rights to:
      </p>

      <ul>
        <li>access your personal information</li>
        <li>request correction, amendment or deletion</li>
        <li>object to or restrict certain processing</li>
        <li>request data portability</li>
        <li>withdraw consent at any time</li>
        <li>opt out of marketing communications.</li>
      </ul>

      <p>
      We will comply with such requests where required by the laws applicable to you.
      </p>

      <h3>
      How to exercise your rights
      </h3>

      <p>
      You may contact us at:
      </p>

      <blockquote>
        Bendelta Pty Ltd (trading as BOLDLY)<br />
        Level 5, 32-36 York Street<br />
        Sydney NSW 2000, Australia<br />
        Phone: +61 2 9232 3401<br />
        Email: <a href="mailto:connect@boldly.app">connect@boldly.app</a>
      </blockquote>

      <p>
      We may require proof of identity before processing your request.
      </p>

      <p>
      If you are located in the EU, UK or a jurisdiction with privacy regulators, you may lodge a complaint with your local Data Protection Authority.
      </p>
      <h2>11. Security</h2>
      <p>We maintain appropriate administrative, technical and organisational measures to protect personal information from loss, misuse, unauthorised access, disclosure, alteration and destruction. However, no system is completely secure and we cannot guarantee absolute security.</p>
    </div>
  );
};

PrivacyPolicy.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

PrivacyPolicy.propTypes = {
  rootClassName: string,
  className: string,
};

export default PrivacyPolicy;
